import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Settings, User } from 'lucide-react';
import { useAuth } from './auth-context';
import { SignInMenuItem } from './sign-in-menu-item';
import { preloadGoogleIdentity } from './use-google-identity';

/**
 * Header account control: an avatar button with a dropdown. Guests get a blank
 * avatar whose menu holds a single entry — the official Google sign-in button
 * (which GIS requires to be visible; see SignInMenuItem). Signed-in users get
 * their picture with the Settings link and sign-out.
 */
export function UserMenu() {
  const { user, loading, signIn, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const openedAtRef = useRef(0);

  // Rapid double-clicks land as open-then-close, which reads as "the menu
  // never appeared" — swallow the closing click while the menu is this young.
  const toggleMenu = () => {
    if (open && Date.now() - openedAtRef.current < 300) return;
    if (!open) openedAtRef.current = Date.now();
    setOpen(!open);
  };

  // Close the menu on an outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (menuRef.current && target instanceof Node && !menuRef.current.contains(target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Warm the GIS script for guests so the sign-in overlay is ready by the
  // time the menu is opened.
  useEffect(() => {
    if (!loading && !user) preloadGoogleIdentity();
  }, [loading, user]);

  // Reserve the avatar footprint so the header doesn't shift when auth resolves.
  if (loading) {
    return <div className="size-9 rounded-full bg-muted" aria-hidden />;
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={toggleMenu}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className={`flex size-9 items-center justify-center overflow-hidden rounded-full shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
          user
            ? 'bg-gradient-to-br from-indigo-500 to-violet-600 text-white ring-1 ring-white/10'
            : 'border border-border bg-muted text-muted-foreground'
        }`}
      >
        {user?.picture ? (
          <img
            src={user.picture}
            alt=""
            className="size-full object-cover"
            referrerPolicy="no-referrer"
            // A native image drag swallows the click (press + slight move =
            // no menu), so clicks on the photo intermittently went dead.
            draggable={false}
          />
        ) : (
          <User className="size-5" />
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-2 w-56 overflow-hidden rounded-lg border border-border bg-card text-card-foreground shadow-md"
        >
          {user ? (
            <>
              <div className="border-b border-border px-3 py-2">
                {user.name && <p className="truncate text-sm font-medium">{user.name}</p>}
                <p className="truncate text-xs text-muted-foreground">{user.email}</p>
              </div>
              <Link
                to="/settings"
                role="menuitem"
                onClick={() => setOpen(false)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
              >
                <Settings className="size-4" />
                Settings
              </Link>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  void signOut();
                }}
                className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
              >
                <LogOut className="size-4" />
                Sign out
              </button>
            </>
          ) : (
            <SignInMenuItem
              onCredential={(credential) => {
                setOpen(false);
                void signIn(credential);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
